import i18next, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

export type I18nAppConfig = {
  appName: string;
  supportedLngs?: string[];
  fallbackLng?: string | string[];
  defaultNS?: string;
  ns?: string[];
  debug?: boolean;
};

export function buildLoadPath(appName: string) {
  // Use appName to construct app-specific locale paths
  // For now, all apps use the same path structure, but this allows for future customization
  console.debug(`Building load path for app: ${appName}`);
  return `/locales/{{lng}}/{{ns}}.json`;
}

export async function initI18n(config: I18nAppConfig) {
  const {
    appName,
    supportedLngs = ['en'],
    fallbackLng = 'en',
    defaultNS = 'common',
    ns = ['common', appName],
    debug = false,
  } = config;

  const options: InitOptions = {
    supportedLngs,
    fallbackLng,
    ns,
    defaultNS,
    returnEmptyString: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    backend: {
      loadPath: buildLoadPath(appName),
    },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      caches: ['localStorage', 'cookie'],
    },
    react: { useSuspense: false },
    debug,
    // Add missing key handling
    saveMissing: debug,
    missingKeyHandler: debug
      ? (lng, ns, key) => {
          console.warn(
            `Missing translation key: ${key} in namespace: ${ns} for language: ${lng}`
          );
        }
      : undefined,
  };

  if (!i18next.isInitialized) {
    try {
      await i18next
        .use(HttpBackend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init(options);
    } catch (error) {
      console.error('❌ i18n initialization failed:', error);
    }
  }

  return i18next;
}

export type { i18next as I18NextInstance };
